// Ordly brand logo — inline SVG, sourced from the official brand artwork
// uploaded by the user (logoUPd.svg + logoicon.svg). Paths are inlined so the
// logo renders crisply at any size without a network round-trip.
//
// Two exports:
//   <OrdlyMark size={N} />   — icon-only (3 bars). Square crop.
//   <OrdlyLogo size={N} />   — icon + "Ordly" wordmark (lockup).
//
// Colors are baked in to match the brand assets:
//   navy  #07152E (st1) — bars + wordmark glyphs
//   mint  #8FCDBA (st2) — middle bar accent
import React from "react";

const NAVY = "#07152E";
const MINT = "#8FCDBA";

// ===== Icon-only (3 bars) =====
// Source: /artifacts/du09ntuf_logoicon.svg
// Tightly cropped viewBox around the 3 bars in the source artwork.
export function OrdlyMark({ size = 32, className = "", "data-testid": testId }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="365 150 200 175"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            data-testid={testId}
            aria-label="Ordly"
            role="img"
        >
            {/* Top bar — navy */}
            <path
                fill={NAVY}
                d="M411.6,161.1c5-.4,10-.2,15-.2,43.8,0,87.7,0,131.5,0-2.6,8.7-6.7,17.5-13.3,24-6.6,6.5-15.9,10.6-25.2,10.4-48.1,0-96.3,0-144.4,0,1.9-6.1,4-11.9,7.9-17,6.7-9.4,16.9-16,28.4-17.1Z"
            />
            {/* Middle bar — mint */}
            <path
                fill={MINT}
                d="M412.6,222c47.6.2,95.3-.2,142.9.1-2.7,11.9-10.3,24.4-21.7,29.7-5.1,2.6-10.5,3.3-16.2,3.2-47.4,0-94.9,0-142.4,0,3.2-11.8,10.2-23.6,21.3-29.4,4.9-2.4,10.5-3.8,16-3.6Z"
            />
            {/* Bottom bar — navy */}
            <path
                fill={NAVY}
                d="M411.6,281.8c48,0,96,0,143.9,0-2.2,8.9-6.2,17.3-12.9,23.8-5.8,5.7-13.8,9.1-21.9,9.4-48.4,0-96.7,0-145.1,0,2.3-12.1,10.2-24.4,21.5-29.9,4.5-2.2,9.5-3.4,14.5-3.3Z"
            />
        </svg>
    );
}

// ===== Full lockup: icon + "Ordly" wordmark =====
// Source: /artifacts/etue1ejb_logoUPd.svg
// Tightly cropped viewBox around the full lockup (icon + wordmark).
// `wordmarkSize` is accepted for back-compat but no longer used (the inlined
// wordmark scales proportionally with `size`).
export function OrdlyLogo({
    size = 36,
    // eslint-disable-next-line no-unused-vars
    wordmarkSize,
    className = "",
    "data-testid": testId,
}) {
    // Source viewBox is 0 0 980 500; the lockup occupies roughly (55,150)→(925,385).
    // Width:height ratio ≈ 870:235 ≈ 3.7:1. `size` is treated as the lockup HEIGHT.
    const height = size;
    const width = Math.round(size * (870 / 235));
    return (
        <svg
            width={width}
            height={height}
            viewBox="55 150 870 235"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            data-testid={testId}
            aria-label="Ordly"
            role="img"
        >
            {/* === icon: 3 bars (left side) === */}
            <path
                fill={NAVY}
                d="M106.8,176.8c5-.4,10-.2,15-.2,43.8,0,87.7,0,131.5,0-2.6,8.7-6.7,17.5-13.3,24-6.6,6.5-15.9,10.6-25.2,10.4-48.1,0-96.3,0-144.4,0,1.9-6.1,4-11.9,7.9-17,6.7-9.4,16.9-16,28.4-17.1Z"
            />
            <path
                fill={MINT}
                d="M107.8,237.7c47.6.2,95.3-.2,142.9.1-2.7,11.9-10.3,24.4-21.7,29.7-5.1,2.6-10.5,3.3-16.2,3.2-47.4,0-94.9,0-142.4,0,3.2-11.8,10.2-23.6,21.3-29.4,4.9-2.4,10.5-3.8,16-3.6Z"
            />
            <path
                fill={NAVY}
                d="M106.7,297.5c48,0,96,0,143.9,0-2.2,8.9-6.2,17.3-12.9,23.8-5.8,5.7-13.8,9.1-21.9,9.4-48.4,0-96.7,0-145.1,0,2.3-12.1,10.2-24.4,21.5-29.9,4.5-2.2,9.5-3.4,14.5-3.3Z"
            />
            {/* === wordmark: O r d l y === */}
            {/* O */}
            <path
                fill={NAVY}
                d="M420.8,315.2c18.4,1.4,37.4-5.5,51.3-17.5,8.7-7.7,15.6-17.5,19.8-28.4,4.7-12.5,5.9-26.6,2.8-39.7-3.6-17.6-14.4-32.9-29.1-43-11.7-8.2-25.6-11.9-39.8-12.5-13.6.2-26.9,3.5-38.6,10.6-18.8,11.2-31.9,31-34.1,52.9-.5,9.4-.4,18.7,2.7,27.8,5.3,16.9,17.3,31.7,32.7,40.4,9.7,5.7,21.1,9,32.3,9.6ZM451.6,158.1c23.5,7.1,44.5,23.9,55.2,46.1,5.1,9.9,7.9,21,9.1,32,1.7,17.6-2.1,35.4-10.5,50.9-7.7,14.9-19.9,27.1-34.4,35.4-28.9,17-66.7,16.4-95-1.4-16.9-10.6-30.6-26.6-37.6-45.3-4.9-12.6-6.9-26.8-5.3-40.2.5-7.3,2.3-14.4,4.9-21.2,10.4-28.8,36-51,65.9-57.5,15.6-3.8,32.3-3,47.7,1.1"
            />
            {/* r */}
            <path
                fill={NAVY}
                d="M600,209.1c0,6.7,0,13.4,0,20-7.5.3-15.5-.5-22.8.9-4.1.9-7.9,3-11.3,5.5-6.4,5.3-10,13.5-9.9,21.8,0,23.4,0,46.9,0,70.3.1.9-.2,1.5-.8,2.1-6.4,0-12.8,0-19.2,0-.2-24.1,0-48.2,0-72.4-.5-20.9,14.2-41.3,34.6-46.5,10-2.7,19.2-1.6,29.3-1.8Z"
            />
            {/* d */}
            <path
                fill={NAVY}
                d="M663,228.4c-12.1,1.8-23.2,9.1-29.5,19.7-4.6,7.2-6.9,15.6-6.5,24.2.4,16.2,11.2,31.4,26.2,37.5,11.9,5.1,26.3,4,37.4-2.7,11.8-7.1,19.7-20,20.6-33.7.8-10.4-2.5-21.2-9.1-29.4-9.1-11.6-24.5-17.9-39.1-15.6ZM711.2,156.1c6.6,0,13.3,0,20,0,.3,20.7,0,41.5.1,62.2,0,37.1.1,74.2,0,111.3-6.7,0-13.4,0-20,0,0-4.6,0-9.3,0-13.9-8.2,7.3-17.6,12.9-28.4,15.2-15.6,3.6-32.4.8-46.1-7.6-20.9-12.6-32.6-38-28.8-62.1,2.5-17.7,13.2-34.1,28.4-43.5,19.4-12.2,45.8-12.5,65.2-.3,3.4,2,6.2,4.8,9.5,6.9.4-22.8-.1-45.6.2-68.4"
            />
            {/* l */}
            <path
                fill={NAVY}
                d="M761.6,156.2c6.6-.2,13.1,0,19.7-.1.3,5.4.1,10.8.2,16.2,0,52.5.1,104.9,0,157.4-6.6,0-13.2,0-19.8,0,0-57.8-.2-115.6,0-173.4Z"
            />
            {/* y */}
            <path
                fill={NAVY}
                d="M820.2,208.7c10.8,29.8,22,59.4,32.6,89.2,11.5-29.7,23.5-59.3,34.9-89.1,7.2,0,14.4,0,21.6,0-5.5,13.1-10.2,26.5-15.6,39.6-9.2,23.8-18.7,47.4-27.9,71.1-6.8,17.1-13.4,34.3-20.7,51.1-7.1-.2-14.2,0-21.2-.1,1.7-5.2,3.9-10.2,5.9-15.2,3.8-9.3,7.5-18.6,11.4-27.8,1.1-2.3-.3-4.4-1-6.7-13.6-37.4-27.4-74.7-41.1-112,7-.1,14,0,21-.1Z"
            />
        </svg>
    );
}
